import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { useContent } from "../contexts/ContentContext";
import LeadForm from "./LeadForm";

function googleMapsUrl(query) {
  const q = encodeURIComponent(query || "");
  return q ? `https://www.google.com/maps/search/?api=1&query=${q}` : "#";
}

export default function Contact() {
  const { content } = useContent();
  const c = content?.contact || {};
  if (c.enabled === false) return null;
  const s = content?.settings || {};
  const waMsg = encodeURIComponent(s.whatsapp_message || "Hi, I'm interested in your courses.");

  const mapsHref = googleMapsUrl(s.map_query || s.address);

  const rows = [
    { icon: Phone, label: "Call us", value: s.phone_display, href: `tel:${s.phone}`, testid: "contact-call", external: false },
    { icon: MessageCircle, label: "WhatsApp", value: s.phone_display, href: `https://wa.me/${s.whatsapp}?text=${waMsg}`, testid: "contact-whatsapp", external: true },
    { icon: Mail, label: "Email", value: s.email, href: `mailto:${s.email}`, testid: "contact-email", external: false },
    { icon: MapPin, label: "Visit", value: s.address, href: mapsHref, testid: "contact-address", external: true, hint: "Tap to open in Google Maps" },
    { icon: Clock, label: "Hours", value: s.hours, href: null, testid: "contact-hours", external: false },
  ];

  return (
    <section id="contact" className="py-20 lg:py-28 bg-secondary/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline">{c.eyebrow}</div>
            <h2 className="font-display mt-3 text-3xl lg:text-4xl font-medium tracking-tight leading-tight">
              {c.heading}
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">{c.body}</p>

            <div className="mt-8 space-y-4">
              {rows.filter((r) => r.value).map((row) => {
                const Wrapper = row.href ? "a" : "div";
                const wrapperProps = row.href ? {
                  href: row.href,
                  target: row.external ? "_blank" : undefined,
                  rel: row.external ? "noopener noreferrer" : undefined,
                } : {};
                return (
                  <Wrapper
                    key={row.label}
                    {...wrapperProps}
                    data-testid={row.testid}
                    className={`flex items-start gap-4 p-4 rounded-xl bg-white border border-border transition-all ${row.href ? "hover:border-primary/30 hover:-translate-y-0.5" : ""}`}
                  >
                    <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <row.icon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">{row.label}</div>
                      <div className="font-display text-base font-medium mt-0.5">{row.value}</div>
                      {row.hint && (
                        <div className="text-[11px] text-primary mt-0.5">{row.hint} ↗</div>
                      )}
                    </div>
                  </Wrapper>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-border bg-white p-7 lg:p-9 h-full">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary">
                <span>{c.form_eyebrow || "Quick Enquiry"}</span>
              </div>
              <h3 className="font-display mt-2 text-2xl lg:text-3xl font-medium tracking-tight">
                {c.form_heading || "Tell us about you — we'll call you back."}
              </h3>
              {c.form_body && (
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.form_body}</p>
              )}
              <div className="mt-6">
                <LeadForm variant="light" testidPrefix="contact-lead" submitLabel={c.form_submit_label || "Send enquiry"} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
